import {
  DrawerHeader,
  DrawerBody,
  Box,
  UnorderedList,
  ListItem,
  DrawerFooter,
  FormLabel,
  Text,
  Input,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaArrowRight } from 'react-icons/fa';

import Button from 'components/Button';
import { AnimatedMascot } from 'components/Mascot';
import { useImportJobs } from 'hooks/jobs/useImportJobs';
import { useFetchImportJobSample } from 'hooks/jobs/userFetchImportJobSample';
import { blobToFile, mimeType } from 'utils/helpers';

interface IUploadJobCSVProps {
  updateUploadStep: (step: number) => void;
  handleFileUploadComplete: (data: any) => void;
  importId: string;
}

const UploadJobCSV: React.FC<IUploadJobCSVProps> = ({ updateUploadStep, importId, handleFileUploadComplete }) => {
  const { t } = useTranslation(['jobsTasks', 'common']);
  const [enableFetchJobSample, setEnableFetchJobSample] = useState<boolean>(false);

  const { status: importJobStatus, mutateAsync: mutateAsyncImportJobs } = useImportJobs();

  const { isInitialLoading } = useFetchImportJobSample({
    enabled: enableFetchJobSample,
    onSettled: () => {
      setEnableFetchJobSample(false);
    },
  });

  const handleDownloadFileClick = () => {
    setEnableFetchJobSample(true);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;
    let file: File = files[0];

    /**  Used for Windows file type bug
     * while uploading csv data when fileType doesnot corresponds to 'text/csv' mimeType, then it will change that type into text/csv
     */
    if (file.type !== 'text/csv') {
      const split_name = file.name.split('.');
      const type = split_name[split_name.length - 1];
      const blob = new Blob([file], { type: mimeType(type) });
      file = blobToFile(blob, file.name);
    }

    if (file) {
      mutateAsyncImportJobs(file).then(data => {
        handleFileUploadComplete(data);
      });
    }
  };

  return (
    <>
      <DrawerHeader>{t('jobsTasks:importJobs')}</DrawerHeader>
      <DrawerBody>
        <Box p="4" background="primary.50" mb="10">
          <Text fontSize="lg" fontWeight="semibold" mb="2" data-testid="mass-import-jobs-instructions">
            {t('jobsTasks:followTheseStepsToMassImportJobs')}:
          </Text>
          <UnorderedList mb="4" data-testid="mass-import-jobs-steps">
            <ListItem>{t('jobsTasks:importJobsStep1')}</ListItem>
            <ListItem>{t('jobsTasks:importJobsStep2')}</ListItem>
            <ListItem>{t('jobsTasks:importJobsStep3')}</ListItem>
          </UnorderedList>
          <Box textAlign="center">
            <Button colorScheme="primary" size="md" isLoading={isInitialLoading} onClick={handleDownloadFileClick}>
              {t('common:downloadTemplate')}
            </Button>
          </Box>
        </Box>

        <FormLabel htmlFor="upload-csv" cursor="pointer">
          <Input
            display="none"
            accept=".csv, text/csv, text/*,"
            id="upload-csv"
            name="upload-csv"
            type="file"
            onChange={handleFileChange}
          />
          <Button
            colorScheme="secondary"
            width="100%"
            pointerEvents="none"
            isLoading={importJobStatus === 'loading'}
            data-testid="upload-csv-button"
          >
            {t('common:uploadCSV')}
          </Button>
        </FormLabel>

        {!importId ? <AnimatedMascot title="Let's do this!" /> : null}
      </DrawerBody>
      {importId ? (
        <DrawerFooter>
          <Button
            variant="secondary-ghost"
            rightIcon={<FaArrowRight />}
            mr="2"
            onClick={() => {
              updateUploadStep(2);
            }}
            data-testid="go-to-mapping-button"
          >
            Go to mapping
          </Button>
        </DrawerFooter>
      ) : null}
    </>
  );
};

export default UploadJobCSV;
